import { useState, useEffect, useContext } from "react";
import {
  FaChartLine,
  FaChartBar,
  FaChartPie,
  FaCalendarAlt,
  FaBox,
  FaWarehouse,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaDollarSign,
  FaClock,
  FaSync,
} from "react-icons/fa";
import { motion } from "framer-motion";
import AuthContext from "../../contexts/AuthContext";
import {
  getInventoryItems,
  getStockMovements,
  getOpeningStock,
  getMainStoreData,
} from "../../utils/inventoryApi";
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  PieChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  Bar,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";

export default function InventoryAnalytics() {
  const { restaurantId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30"); // days

  const [analytics, setAnalytics] = useState({
    // Summary Stats
    totalItems: 0,
    totalValue: 0,
    lowStockItems: 0,
    overStockItems: 0,
    totalMovements: 0,

    // Stock Distribution
    stockDistribution: [],
    categoryDistribution: [],

    // Movement Analytics
    movementTrends: [],
    movementByType: [],
    movementByLocation: [],

    // Top Items
    topItemsByValue: [],
    topItemsByQuantity: [],
    lowStockAlerts: [],

    // Performance Metrics
    stockTurnover: [],
    averageStockLevel: 0,
    stockEfficiency: 0,

    // Time-based Data
    dailyMovements: [],
    weeklyTrends: [],
    monthlySummary: [],

    // Additional Analytics
    stockVelocity: [],
    supplierPerformance: [],
    wasteAnalytics: [],
    costAnalysis: [],
  });
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884D8",
    "#82CA9D",
  ];

  useEffect(() => {
    fetchAnalytics();
  }, [restaurantId, timeRange]);

  async function fetchAnalytics() {
    setLoading(true);
    setError("");

    // Add delay for retry attempts
    if (retryCount > 0) {
      const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 10000); // Exponential backoff, max 10s
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    try {
      const [items, movements, openingStock, mainStoreData] = await Promise.all(
        [
          getInventoryItems(restaurantId),
          getStockMovements(restaurantId),
          getOpeningStock(restaurantId, "main"),
          getMainStoreData(restaurantId),
        ]
      );

      // Calculate summary statistics
      const totalItems = mainStoreData.length;
      const totalValue = mainStoreData.reduce(
        (sum, item) => sum + (item.totalValue || 0),
        0
      );
      const lowStockItems = mainStoreData.filter(
        (item) => (item.totalQty || 0) < 10
      ).length;
      const overStockItems = mainStoreData.filter(
        (item) => (item.totalQty || 0) > 100
      ).length;
      const totalMovements = movements.length;

      // Stock Distribution by Status
      const stockDistribution = [
        { name: "Low Stock", value: lowStockItems, color: "#FF6B6B" },
        {
          name: "In Stock",
          value: mainStoreData.filter(
            (item) => (item.totalQty || 0) >= 10 && (item.totalQty || 0) <= 100
          ).length,
          color: "#4ECDC4",
        },
        { name: "Over Stock", value: overStockItems, color: "#FFE66D" },
      ];

      // Category Distribution (if categories exist)
      const categoryMap = {};
      mainStoreData.forEach((item) => {
        const category = item.category || "General";
        if (!categoryMap[category]) categoryMap[category] = 0;
        categoryMap[category] += item.totalQty || 0;
      });
      const categoryDistribution = Object.entries(categoryMap).map(
        ([name, value], idx) => ({
          name,
          value,
          color: COLORS[idx % COLORS.length],
        })
      );

      // Movement Trends (last 30 days by default)
      const days = parseInt(timeRange);
      const movementTrendsMap = {};
      const now = new Date();

      // Initialize dates
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split("T")[0];
        movementTrendsMap[dateStr] = {
          date: dateStr,
          incoming: 0,
          outgoing: 0,
          transfers: 0,
          total: 0,
        };
      }

      // Process movements
      movements.forEach((movement) => {
        const movementDate = movement.createdAt
          ? movement.createdAt.toDate
            ? movement.createdAt.toDate().toISOString().split("T")[0]
            : typeof movement.createdAt === "object" &&
              movement.createdAt._seconds
            ? new Date(movement.createdAt._seconds * 1000)
                .toISOString()
                .split("T")[0]
            : movement.createdAt.split("T")[0]
          : null;

        if (movementDate && movementTrendsMap[movementDate]) {
          const quantity = movement.quantity || 0;
          if (movement.type === "IN") {
            movementTrendsMap[movementDate].incoming += quantity;
          } else if (movement.type === "OUT") {
            movementTrendsMap[movementDate].outgoing += quantity;
          } else if (movement.type === "TRANSFER") {
            movementTrendsMap[movementDate].transfers += quantity;
          }
          movementTrendsMap[movementDate].total += quantity;
        }
      });

      const movementTrends = Object.values(movementTrendsMap).sort((a, b) =>
        a.date.localeCompare(b.date)
      );

      // Movement by Type
      const movementByType = [
        {
          name: "Incoming",
          value: movements.filter((m) => m.type === "IN").length,
          color: "#4ECDC4",
        },
        {
          name: "Outgoing",
          value: movements.filter((m) => m.type === "OUT").length,
          color: "#FF6B6B",
        },
        {
          name: "Transfers",
          value: movements.filter((m) => m.type === "TRANSFER").length,
          color: "#45B7D1",
        },
      ];

      // Movement by Location
      const locationMap = {};
      movements.forEach((m) => {
        const location = m.toLocation || "Unknown";
        if (!locationMap[location]) locationMap[location] = 0;
        locationMap[location]++;
      });
      const movementByLocation = Object.entries(locationMap).map(
        ([name, value], idx) => ({
          name,
          value,
          color: COLORS[idx % COLORS.length],
        })
      );

      // Top Items by Value
      const topItemsByValue = [...mainStoreData]
        .sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0))
        .slice(0, 10)
        .map((item) => ({
          name: item.prodName,
          value: item.totalValue || 0,
          quantity: item.totalQty || 0,
        }));

      // Top Items by Quantity
      const topItemsByQuantity = [...mainStoreData]
        .sort((a, b) => (b.totalQty || 0) - (a.totalQty || 0))
        .slice(0, 10)
        .map((item) => ({
          name: item.prodName,
          quantity: item.totalQty || 0,
          value: item.totalValue || 0,
        }));

      // Low Stock Alerts
      const lowStockAlerts = mainStoreData
        .filter((item) => (item.totalQty || 0) < 10)
        .map((item) => ({
          name: item.prodName,
          current: item.totalQty || 0,
          threshold: 10,
          percentage: Math.round(((item.totalQty || 0) / 10) * 100),
          value: item.totalValue || 0,
        }));

      // Stock Turnover (simplified calculation)
      const stockTurnover = mainStoreData
        .map((item) => ({
          name: item.prodName,
          turnover: Math.random() * 5 + 1, // Placeholder calculation
          stockLevel: item.totalQty || 0,
        }))
        .slice(0, 10);

      // Performance Metrics
      const averageStockLevel = totalItems
        ? Math.round(
            mainStoreData.reduce((sum, item) => sum + (item.totalQty || 0), 0) /
              totalItems
          )
        : 0;

      const stockEfficiency = totalItems
        ? Math.round(
            ((totalItems - lowStockItems - overStockItems) / totalItems) * 100
          )
        : 0;

      // Additional Analytics
      const stockVelocity = mainStoreData
        .map((item) => ({
          name: item.prodName,
          velocity: Math.random() * 10 + 1, // Placeholder for stock velocity
          stockLevel: item.totalQty || 0,
          value: item.totalValue || 0,
        }))
        .slice(0, 10);

      const supplierPerformance = mainStoreData
        .filter((item) => item.suppliers && item.suppliers.length > 0)
        .map((item) => ({
          name: item.suppliers[0] || "Unknown",
          performance: Math.random() * 100 + 50, // Placeholder
          items: 1,
          value: item.totalValue || 0,
        }))
        .slice(0, 10);

      const wasteAnalytics = movements
        .filter((m) => m.toLocation === "WASTE")
        .map((m) => ({
          name: m.itemName,
          quantity: m.quantity || 0,
          value: (m.quantity || 0) * 10, // Placeholder value calculation
          reason: m.reason || "Unknown",
        }))
        .slice(0, 10);

      const costAnalysis = mainStoreData
        .map((item) => ({
          name: item.prodName,
          cost: item.totalValue || 0,
          quantity: item.totalQty || 0,
          avgCost: item.totalQty ? (item.totalValue || 0) / item.totalQty : 0,
        }))
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 10);

      // Weekly Trends (last 4 weeks)
      const weeklyTrends = [];
      for (let i = 3; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - i * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const weekMovements = movements.filter((m) => {
          const movementDate = m.createdAt
            ? m.createdAt.toDate
              ? m.createdAt.toDate()
              : typeof m.createdAt === "object" && m.createdAt._seconds
              ? new Date(m.createdAt._seconds * 1000)
              : new Date(m.createdAt)
            : null;

          return (
            movementDate && movementDate >= weekStart && movementDate <= weekEnd
          );
        });

        weeklyTrends.push({
          week: `Week ${4 - i}`,
          movements: weekMovements.length,
          value: weekMovements.reduce((sum, m) => sum + (m.quantity || 0), 0),
        });
      }

      setAnalytics({
        totalItems,
        totalValue,
        lowStockItems,
        overStockItems,
        totalMovements,
        stockDistribution,
        categoryDistribution,
        movementTrends,
        movementByType,
        movementByLocation,
        topItemsByValue,
        topItemsByQuantity,
        lowStockAlerts,
        stockTurnover,
        averageStockLevel,
        stockEfficiency,
        weeklyTrends,
        stockVelocity,
        supplierPerformance,
        wasteAnalytics,
        costAnalysis,
      });

      // Reset retry count on successful fetch
      setRetryCount(0);
    } catch (err) {
      console.error("Analytics fetch error:", err);

      // Check for specific Firebase quota errors
      if (err.message && err.message.includes("Quota exceeded")) {
        setError(
          "Firebase quota exceeded. Please try again later or contact support."
        );
        setRetryCount((prev) => prev + 1);
      } else if (err.message && err.message.includes("RESOURCE_EXHAUSTED")) {
        setError("Database quota exceeded. Please try again later.");
        setRetryCount((prev) => prev + 1);
      } else {
        setError("Failed to fetch analytics data. Please try again.");
        setRetryCount((prev) => prev + 1);
      }
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
              Inventory Analytics
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Comprehensive insights into your inventory performance
            </p>
          </div>
        </div>

        {/* Error Display */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <div className="flex flex-col items-center">
            <FaExclamationTriangle className="text-red-500 text-4xl mb-4" />
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Unable to Load Analytics
            </h3>
            <p className="text-red-600 dark:text-red-300 mb-4 max-w-md">
              {error}
            </p>
            {retryCount > 0 && (
              <p className="text-sm text-red-500 dark:text-red-400 mb-4">
                Retry attempt: {retryCount}
              </p>
            )}
            <div className="flex gap-4">
              <button
                onClick={fetchAnalytics}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <FaSync /> Try Again
              </button>
              <button
                onClick={() => setError("")}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
            Inventory Analytics
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1 sm:mt-2">
            Comprehensive insights into your inventory performance
          </p>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Inventory Value
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                ₹{analytics.totalValue.toLocaleString()}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900 rounded-full">
              <FaChartLine className="text-green-600 dark:text-green-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Items
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {analytics.totalItems}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <FaChartBar className="text-blue-600 dark:text-blue-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Avg Stock Level
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {analytics.averageStockLevel}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
              <FaChartPie className="text-purple-600 dark:text-purple-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                Low Stock Items
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600 dark:text-red-400">
                {analytics.lowStockItems}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Need attention
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900 rounded-full">
              <FaExclamationTriangle className="text-red-600 dark:text-red-400 text-base sm:text-lg lg:text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Enhanced Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Stock Movement Trends - Area Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <FaArrowUp className="text-blue-500" />
              Stock Movement Trends ({timeRange} days)
            </h3>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                Incoming
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                Outgoing
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Transfers
              </span>
            </div>
          </div>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="sm:h-[300px]"
          >
            <AreaChart data={analytics.movementTrends}>
              <defs>
                <linearGradient
                  id="incomingGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient
                  id="outgoingGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient
                  id="transfersGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="date"
                stroke="#6B7280"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                }
              />
              <YAxis stroke="#6B7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "none",
                  borderRadius: "8px",
                  color: "#F9FAFB",
                }}
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value, name) => [
                  value,
                  name.charAt(0).toUpperCase() + name.slice(1),
                ]}
              />
              <Area
                type="monotone"
                dataKey="incoming"
                stackId="1"
                stroke="#3B82F6"
                fill="url(#incomingGradient)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="outgoing"
                stackId="1"
                stroke="#EF4444"
                fill="url(#outgoingGradient)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="transfers"
                stackId="1"
                stroke="#10B981"
                fill="url(#transfersGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Stock Status Distribution - Enhanced Pie Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <FaChartPie className="text-purple-500" />
              Stock Status Distribution
            </h3>
            <div className="text-right">
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-white">
                {analytics.totalItems}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Total Items
              </p>
            </div>
          </div>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="sm:h-[300px]"
          >
            <PieChart>
              <Pie
                data={analytics.stockDistribution}
                cx="50%"
                cy="50%"
                innerRadius={40}
                className="sm:innerRadius={60}"
                outerRadius={80}
                className="sm:outerRadius={100}"
                paddingAngle={5}
                dataKey="value"
                label={({ name, value, percent }) =>
                  `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                }
                labelLine={true}
              >
                {analytics.stockDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "none",
                  borderRadius: "8px",
                  color: "#F9FAFB",
                }}
                formatter={(value, name) => [value, name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Top Items by Value - Horizontal Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <FaDollarSign className="text-green-500" />
              Top Items by Value
            </h3>
            <div className="text-right">
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 dark:text-green-400">
                ₹{analytics.totalValue.toLocaleString()}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Total Value
              </p>
            </div>
          </div>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="sm:h-[300px]"
          >
            <BarChart
              data={analytics.topItemsByValue}
              layout="horizontal"
              margin={{ left: 80 }}
              className="sm:margin={{ left: 100 }}"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                type="number"
                stroke="#6B7280"
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={80}
                className="sm:width={100}"
                stroke="#6B7280"
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "none",
                  borderRadius: "8px",
                  color: "#F9FAFB",
                }}
                formatter={(value) => [`₹${value.toLocaleString()}`, "Value"]}
                labelFormatter={(label) => `Item: ${label}`}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {analytics.topItemsByValue.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`hsl(${200 + index * 20}, 70%, 60%)`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Movement by Type - Donut Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <FaChartBar className="text-blue-500" />
              Movement by Type
            </h3>
            <div className="text-right">
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 dark:text-blue-400">
                {analytics.totalMovements}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Total Movements
              </p>
            </div>
          </div>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="sm:h-[300px]"
          >
            <PieChart>
              <Pie
                data={analytics.movementByType}
                cx="50%"
                cy="50%"
                innerRadius={30}
                className="sm:innerRadius={40}"
                outerRadius={60}
                className="sm:outerRadius={80}"
                paddingAngle={5}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}`}
                labelLine={true}
              >
                {analytics.movementByType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "none",
                  borderRadius: "8px",
                  color: "#F9FAFB",
                }}
                formatter={(value, name) => [value, name]}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Additional Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Weekly Trends - Line Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.8 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <FaClock className="text-purple-500" />
              Weekly Movement Trends
            </h3>
            <div className="text-right">
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-600 dark:text-purple-400">
                {analytics.weeklyTrends.reduce(
                  (sum, week) => sum + week.movements,
                  0
                )}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Total Weekly
              </p>
            </div>
          </div>
          <ResponsiveContainer
            width="100%"
            height={250}
            className="sm:h-[300px]"
          >
            <LineChart data={analytics.weeklyTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="week" stroke="#6B7280" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6B7280" />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1F2937",
                  border: "none",
                  borderRadius: "8px",
                  color: "#F9FAFB",
                }}
                formatter={(value, name) => [
                  value,
                  name === "movements" ? "Movements" : "Value",
                ]}
              />
              <Line
                type="monotone"
                dataKey="movements"
                stroke="#8B5CF6"
                strokeWidth={3}
                dot={{ fill: "#8B5CF6", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "#8B5CF6", strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "#10B981", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Stock Efficiency Gauge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.9 }}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <FaArrowUp className="text-emerald-500" />
              Stock Efficiency
            </h3>
            <div className="text-right">
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {analytics.stockEfficiency}%
              </p>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Efficiency
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center h-48 sm:h-64">
            <div className="relative">
              <svg
                width="160"
                height="160"
                className="sm:w-[200px] sm:h-[200px] transform -rotate-90"
              >
                <circle
                  cx="80"
                  className="sm:cx={100}"
                  cy="80"
                  className="sm:cy={100}"
                  r="64"
                  className="sm:r={80}"
                  stroke="#E5E7EB"
                  strokeWidth="10"
                  className="sm:strokeWidth={12}"
                  fill="none"
                />
                <circle
                  cx="80"
                  className="sm:cx={100}"
                  cy="80"
                  className="sm:cy={100}"
                  r="64"
                  className="sm:r={80}"
                  stroke={
                    analytics.stockEfficiency > 70
                      ? "#10B981"
                      : analytics.stockEfficiency > 40
                      ? "#F59E0B"
                      : "#EF4444"
                  }
                  strokeWidth="10"
                  className="sm:strokeWidth={12}"
                  fill="none"
                  strokeDasharray={`${
                    (analytics.stockEfficiency / 100) * 402.1
                  } 402.1`}
                  className="sm:strokeDasharray={`${
                    (analytics.stockEfficiency / 100) * 502.4
                  } 502.4`}"
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
                    {analytics.stockEfficiency}%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    Optimal Stock
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Low Stock Alerts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 1.0 }}
        className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 lg:p-6 shadow-lg border border-gray-200 dark:border-slate-700"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <FaExclamationTriangle className="text-red-500" />
            Low Stock Alerts
          </h3>
          <div className="text-right">
            <p className="text-lg sm:text-xl lg:text-2xl font-bold text-red-600 dark:text-red-400">
              {analytics.lowStockItems}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Items Need Attention
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {analytics.lowStockAlerts && analytics.lowStockAlerts.length > 0 ? (
            analytics.lowStockAlerts.map((item, index) => (
              <div
                key={index}
                className="p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-800 dark:text-white text-sm">
                    {item.name}
                  </h4>
                  <span className="text-xs sm:text-sm text-red-600 dark:text-red-400 font-medium">
                    {item.percentage}%
                  </span>
                </div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Current: {item.current} | Min: {item.min}
                </div>
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full"
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400">
              <FaExclamationTriangle className="text-3xl sm:text-4xl mx-auto mb-2 text-green-500" />
              <p className="text-sm sm:text-base">
                No low stock alerts! All items are well stocked.
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Performance Metrics Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 1.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
      >
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm opacity-90">Stock Efficiency</p>
              <p className="text-2xl sm:text-3xl font-bold">
                {analytics.stockEfficiency}%
              </p>
              <p className="text-xs opacity-75 mt-1">Optimal stock levels</p>
            </div>
            <FaArrowUp className="text-2xl sm:text-3xl opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm opacity-90">Avg Stock Level</p>
              <p className="text-2xl sm:text-3xl font-bold">
                {analytics.averageStockLevel}
              </p>
              <p className="text-xs opacity-75 mt-1">Per item average</p>
            </div>
            <FaChartBar className="text-2xl sm:text-3xl opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-xl p-4 sm:p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm opacity-90">Movement Rate</p>
              <p className="text-2xl sm:text-3xl font-bold">
                {Math.round(
                  analytics.totalMovements / Math.max(parseInt(timeRange), 1)
                )}
              </p>
              <p className="text-xs opacity-75 mt-1">Per day average</p>
            </div>
            <FaChartLine className="text-2xl sm:text-3xl opacity-80" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
